import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  ChevronDown,
  HelpCircle,
  Plus,
  Settings,
  Edit,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { invoiceService, CreateInvoiceRequest, InvoiceItem } from '@/services/invoice.service';
import { clientService, Client } from '@/services/client.service';
import { toast } from 'sonner';

interface CreateInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvoiceCreated?: (invoice: any) => void;
}

export function CreateInvoiceDialog({
  open,
  onOpenChange,
  onInvoiceCreated,
}: CreateInvoiceDialogProps) {
  const getCurrencySymbol = (code: string | undefined) => {
    switch ((code || 'USD').toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'AED': return 'د.إ';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      default: return code || '$';
    }
  };
  const [formData, setFormData] = useState<CreateInvoiceRequest>({
    customerId: '',
    invoiceNumber: 'INV-04',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    preventOverdueReminders: false,
    tags: [],
    paymentModes: [],
    currency: 'USD',
    saleAgent: 'Ravi Usadadiya',
    isRecurring: false,
    discountType: 'No discount',
    adminNote: '',
    status: undefined,
    items: [
      {
        description: '',
        longDescription: '',
        quantity: 1,
        unit: 'Unit',
        rate: 0,
        tax: 'No Tax',
        amount: 0,
      }
    ],
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [quantityDisplay, setQuantityDisplay] = useState<'qty' | 'hours' | 'qty_hours'>('qty');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState<string>('04');
  const [customerError, setCustomerError] = useState<string>('');
  const [statusError, setStatusError] = useState<string>('');

  // Function to get next invoice number from existing invoices
  const getNextInvoiceNumber = async () => {
    try {
      const invoices = await invoiceService.getInvoices();
      if (invoices.data && invoices.data.length > 0) {
        // Extract numbers from invoice numbers like "INV-01", "INV-02", etc.
        const numbers = invoices.data
          .map(invoice => {
            const match = invoice.invoiceNumber.match(/INV-(\d+)/);
            return match ? parseInt(match[1]) : 0;
          })
          .filter(num => num > 0);

        if (numbers.length > 0) {
          const maxNumber = Math.max(...numbers);
          const nextNumber = String(maxNumber + 1).padStart(2, '0');
          setNextInvoiceNumber(nextNumber);
        }
      }
    } catch (error) {
      console.error('Error fetching invoices for next number:', error);
      // Keep default '04' if API fails
    }
  };
  const invoiceDateRef = useRef<HTMLInputElement>(null);
  const dueDateRef = useRef<HTMLInputElement>(null);

  // Debug: Log clients state changes
  useEffect(() => {
    console.log('🔍 Clients state updated:', clients);
    console.log('📊 Clients count:', clients.length);
    if (clients.length > 0) {
      console.log('👥 First client:', clients[0]);
    }
  }, [clients]);

  useEffect(() => {
    if (open) {
      loadInitialData();
      getNextInvoiceNumber();
      setCustomerError('');
      setStatusError('');
    }
  }, [open]);

  const loadInitialData = async () => {
    try {
      console.log('🔄 Loading initial data...');

      // Load clients separately to handle errors better
      setLoadingClients(true);
      try {
        const clientsResponse = await clientService.getClients({ limit: 1000 });
        console.log('📊 Clients response:', clientsResponse);
        console.log('📊 Clients data:', clientsResponse.data);
        console.log('📊 Number of clients:', clientsResponse.data?.length || 0);

        // Handle different response formats
        const clientsData = clientsResponse.data || clientsResponse || [];
        console.log('📊 Final clients data:', clientsData);

        if (Array.isArray(clientsData) && clientsData.length > 0) {
          setClients(clientsData);
          console.log('✅ Successfully loaded clients:', clientsData.length);
        } else {
          setClients([]);
          console.log('⚠️ No clients found in API response');
        }
      } catch (clientError) {
        console.error('❌ Error loading clients:', clientError);
        setClients([]);
        toast.error('Failed to load clients from API');
      } finally {
        setLoadingClients(false);
      }

      // Skip loading payment modes, tax rates, sale agents, and discount types

    } catch (error) {
      console.error('❌ Unexpected error loading initial data:', error);
      toast.error('Failed to load form data');

      // Set empty arrays as fallback
      setClients([]);
    }
  };

  // Removed generate-number API call due to failing endpoint (404). Invoice number can be entered manually.

  const handleInputChange = (field: keyof CreateInvoiceRequest, value: any) => {
    console.log('🔄 Field changed:', field, 'Value:', value);
    if (field === 'customerId') {
      const client = clients.find(c => (c.id === value) || (c._id === value));
      console.log('👤 Selected client:', client);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...(formData.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };

    // Calculate amount when quantity, rate, or tax changes
    if (field === 'quantity' || field === 'rate' || field === 'tax') {
      const quantity = field === 'quantity' ? value : newItems[index].quantity;
      const rate = field === 'rate' ? value : newItems[index].rate;
      const tax = field === 'tax' ? value : newItems[index].tax;

      // Calculate base amount (quantity * rate)
      const baseAmount = quantity * rate;

      // Calculate tax amount
      let taxAmount = 0;
      if (tax && tax !== 'No Tax') {
        const taxPercentage = parseFloat(tax.replace('%', ''));
        if (!isNaN(taxPercentage)) {
          taxAmount = (baseAmount * taxPercentage) / 100;
        }
      }

      // Total amount = base amount + tax
      newItems[index].amount = baseAmount + taxAmount;
    }
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleBillToChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      billToAddress: {
        address1: value,
        address2: prev.billToAddress?.address2 || '',
        city: prev.billToAddress?.city || '',
        state: prev.billToAddress?.state || '',
        country: prev.billToAddress?.country || '',
        postalCode: prev.billToAddress?.postalCode || '',
      },
    }));
  };

  const handleShipToChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      shipToAddress: {
        address1: value,
        address2: prev.shipToAddress?.address2 || '',
        city: prev.shipToAddress?.city || '',
        state: prev.shipToAddress?.state || '',
        country: prev.shipToAddress?.country || '',
        postalCode: prev.shipToAddress?.postalCode || '',
      },
    }));
  };

  const addItem = () => {
    const unitLabel = quantityDisplay === 'hours' ? 'Hours' : (quantityDisplay === 'qty_hours' ? 'Qty/Hours' : 'Unit');
    setFormData(prev => ({
      ...prev,
      items: [
        ...(prev.items || []),
        {
          description: '',
          longDescription: '',
          quantity: 1,
          unit: unitLabel,
          rate: 0,
          tax: 'No Tax',
          amount: 0,
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    if (formData.items && formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: (prev.items || []).filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (saveAsDraft: boolean = false) => {
    setLoading(true);
    try {
      let hasError = false;

      if (!formData.customerId) {
        setCustomerError('Customer is required');
        hasError = true;
      } else {
        setCustomerError('');
      }

      if (!formData.status) {
        setStatusError('Status is required');
        hasError = true;
      } else {
        setStatusError('');
      }

      if (hasError) {
        setLoading(false);
        return;
      }
      // Build minimal payload as requested by backend
      const payload: any = {
        customerId: formData.customerId,
        invoiceNumber: `INV-${nextInvoiceNumber}`,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        preventOverdueReminders: formData.preventOverdueReminders,
        paymentModes: formData.paymentModes,
        currency: formData.currency,
        status: formData.status,
        items: (formData.items || []).map(i => ({
          description: i.description,
          longDescription: i.longDescription,
          quantity: i.quantity,
          unit: i.unit,
          rate: i.rate,
          tax: i.tax,
          amount: i.amount,
        })),
        billToAddress: formData.billToAddress?.address1 ? { address: formData.billToAddress.address1 } : undefined,
        shipToAddress: formData.shipToAddress?.address1 ? { address: formData.shipToAddress.address1 } : undefined,
      };

      const invoice = await invoiceService.createInvoice(payload as any);
      try {
        if (invoice && (invoice as any).id) {
          localStorage.setItem(`invoiceQuantityDisplay:${(invoice as any).id}`, quantityDisplay);
        }
      } catch { }
      toast.success(saveAsDraft ? 'Invoice saved as draft' : 'Invoice created successfully');
      onInvoiceCreated?.(invoice);
      // Reset form after successful creation and increment invoice number
      const currentNumber = parseInt(nextInvoiceNumber);
      setNextInvoiceNumber(String(currentNumber + 1).padStart(2, '0'));
      setFormData({
        customerId: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date().toISOString().split('T')[0],
        preventOverdueReminders: false,
        tags: [],
        paymentModes: [],
        currency: 'USD',
        saleAgent: 'Ravi Usadadiya',
        isRecurring: false,
        discountType: 'No discount',
        adminNote: '',
        status: undefined,
        items: [{ description: '', longDescription: '', quantity: 1, unit: 'Unit', rate: 0, tax: 'No Tax', amount: 0 }],
      });
      setQuantityDisplay('qty');
      setCustomerError('');
      setStatusError('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating invoice:', error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        // Reset form when closing without saving
        setFormData({
          customerId: '',
          invoiceNumber: '',
          invoiceDate: new Date().toISOString().split('T')[0],
          dueDate: new Date().toISOString().split('T')[0],
          preventOverdueReminders: false,
          tags: [],
          paymentModes: [],
          currency: 'USD',
          saleAgent: 'Ravi Usadadiya',
          isRecurring: false,
          discountType: 'No discount',
          adminNote: '',
          items: [{ description: '', longDescription: '', quantity: 1, unit: 'Unit', rate: 0, tax: 'No Tax', amount: 0 }],
        });
        setQuantityDisplay('qty');
        setCustomerError('');
        setStatusError('');
      }
      onOpenChange?.(newOpen);
    }}>
      <DialogContent className="sm:max-w-[1400px] h-[95vh] flex flex-col p-0 rounded-xl overflow-hidden bg-white custom-date-styles">
        <DialogHeader className="px-8 pb-0 pt-8">
          <DialogTitle className="text-2xl font-bold text-gray-900">Create New Invoice</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new invoice with customer details, items, and billing information.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6 space-y-8 bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className='space-y-2'>
                    <Label htmlFor="customer" className="text-sm font-medium text-gray-700">
                      * Customer
                    </Label>
                    <Select value={formData.customerId} onValueChange={(value) => {
                      if (value) {
                        setCustomerError('');
                      } else {
                        setCustomerError('Customer is required');
                      }
                      handleInputChange('customerId', value);
                    }}>
                      <SelectTrigger className="w-full h-10 border-gray-300 rounded-md">
                        <SelectValue placeholder="Select and begin typing" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingClients ? (
                          <div className="p-2 text-sm text-gray-500 text-center">
                            Loading clients from API...
                          </div>
                        ) : clients.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500 text-center">
                            No clients available from API
                          </div>
                        ) : (
                          clients.map((client) => {
                            const clientId = client.id || client._id || '';
                            console.log('🎯 Rendering client:', client.name, 'ID:', clientId);
                            return (
                              <SelectItem key={clientId} value={clientId}>
                                <div className="flex items-center justify-between w-full">
                                  <span className="font-medium">{client.name}</span>
                                  <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                    {customerError && (
                      <div className="text-red-600 text-xs mt-1">{customerError}</div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Edit className="h-4 w-4 text-gray-500" />
                        <Label className="text-sm font-medium text-gray-700">Bill To</Label>
                      </div>
                      <Textarea
                        placeholder="Enter billing address"
                        value={formData.billToAddress?.address1 || ''}
                        onChange={(e) => handleBillToChange(e.target.value)}
                        className="min-h-[100px] resize-y border-gray-300"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-gray-700">Ship to</Label>
                      <Textarea
                        placeholder="Enter shipping address"
                        value={formData.shipToAddress?.address1 || ''}
                        onChange={(e) => handleShipToChange(e.target.value)}
                        className="min-h-[100px] resize-y border-gray-300"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="invoiceNumber" className="text-sm font-medium text-gray-700">
                      * Invoice Number
                    </Label>
                    <HelpCircle className="h-4 w-4 text-gray-500" />
                  </div>
                  <div className="flex gap-2">
                    <Input value="INV-" disabled className="w-16 h-10 bg-gray-100 border-gray-300" />
                    <Input
                      value={nextInvoiceNumber}
                      disabled
                      className="h-10 border-gray-300 bg-gray-100"
                    />
                    <Input value="10 / 2025" disabled className="w-[6rem] h-10 bg-gray-100 border-gray-300 text-center" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="invoiceDate" className="text-sm font-medium text-gray-700">
                      * Invoice Date
                    </Label>
                    <div className="flex items-center h-10 border border-gray-300 rounded-md overflow-hidden bg-white">
                      <Input
                        id="invoiceDate"
                        type="date"
                        ref={invoiceDateRef}
                        value={formData.invoiceDate}
                        onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                        className="custom-date-input flex-1 h-full border-0 focus-visible:ring-0 focus:outline-none px-3 bg-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => invoiceDateRef.current?.showPicker?.()}
                        className="w-10 h-full flex items-center justify-center border-l border-gray-300 bg-gray-50 cursor-pointer"
                        aria-label="Open invoice date picker"
                      >
                        <Calendar className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dueDate" className="text-sm font-medium text-gray-700">Due Date</Label>
                    <div className="flex items-center h-10 border border-gray-300 rounded-md overflow-hidden bg-white">
                      <Input
                        id="dueDate"
                        type="date"
                        ref={dueDateRef}
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        className="custom-date-input flex-1 h-full border-0 focus-visible:ring-0 focus:outline-none px-3 bg-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => dueDateRef.current?.showPicker?.()}
                        className="w-10 h-full flex items-center justify-center border-l border-gray-300 bg-gray-50 cursor-pointer"
                        aria-label="Open due date picker"
                      >
                        <Calendar className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 pt-2">
                  <Checkbox id="preventOverdueReminders" checked={formData.preventOverdueReminders} onCheckedChange={(checked) => handleInputChange('preventOverdueReminders', checked)} className="border-gray-300" />
                  <Label htmlFor="preventOverdueReminders" className="text-sm text-gray-700">Prevent sending overdue reminders for this invoice</Label>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Allowed payment modes for this invoice</Label>
                  <Select value={formData.paymentModes?.[0] || ''} onValueChange={(value) => handleInputChange('paymentModes', [value])}>
                    <SelectTrigger className="h-10 border-gray-300">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Credit Card">Credit Card</SelectItem>
                      <SelectItem value="Debit Card">Debit Card</SelectItem>
                      <SelectItem value="UPI (Google Pay, PhonePe, Paytm, etc.)">UPI (Google Pay, PhonePe, Paytm, etc.)</SelectItem>
                      <SelectItem value="Bank Transfer (NEFT/RTGS/IMPS)">Bank Transfer (NEFT/RTGS/IMPS)</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Online Payment Gateway">Online Payment Gateway</SelectItem>
                      <SelectItem value="Wallet Payment">Wallet Payment</SelectItem>
                      <SelectItem value="Apple Pay">Apple Pay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">* Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                    <SelectTrigger className="h-10 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD $</SelectItem>
                      <SelectItem value="EUR">EUR €</SelectItem>
                      <SelectItem value="GBP">GBP £</SelectItem>
                      <SelectItem value="INR">INR ₹</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">* Status</Label>
                  <Select value={formData.status || ''} onValueChange={(value) => { setStatusError(''); handleInputChange('status', value); }}>
                    <SelectTrigger className="h-10 border-gray-300">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                  {statusError && (
                    <div className="text-red-600 text-xs mt-1">{statusError}</div>
                  )}
                </div>

              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex items-center gap-2 h-10 border-gray-300">
                        Add Item
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={addItem}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add New Item
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex items-center gap-2 h-10 border-gray-300">
                        Bill Tasks
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Bill Time Tracking
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Removed standalone plus button as requested */}
                </div>

                <div className="flex items-center gap-6">
                  <Label className="text-sm font-medium text-gray-700">Show quantity as:</Label>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center space-x-2">
                      <input type="radio" id="qty" name="quantityDisplay" value="qty" checked={quantityDisplay === 'qty'} onChange={(e) => { setQuantityDisplay(e.target.value as 'qty'); setFormData(prev => ({ ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Unit' })) })); }} className="text-blue-600" />
                      <Label htmlFor="qty" className="text-sm text-gray-700">Qty</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="radio" id="hours" name="quantityDisplay" value="hours" checked={quantityDisplay === 'hours'} onChange={(e) => { setQuantityDisplay(e.target.value as 'hours'); setFormData(prev => ({ ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Hours' })) })); }} className="text-blue-600" />
                      <Label htmlFor="hours" className="text-sm text-gray-700">Hours</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="radio" id="qty_hours" name="quantityDisplay" value="qty_hours" checked={quantityDisplay === 'qty_hours'} onChange={(e) => { setQuantityDisplay(e.target.value as 'qty_hours'); setFormData(prev => ({ ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Qty/Hours' })) })); }} className="text-blue-600" />
                      <Label htmlFor="qty_hours" className="text-sm text-gray-700">Qty/Hours</Label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="flex items-center gap-1 text-gray-700 font-medium">
                        Item
                        <AlertCircle className="h-3 w-3 text-gray-500" />
                      </TableHead>
                      <TableHead className="text-gray-700 font-medium">Description</TableHead>
                      <TableHead className="text-gray-700 font-medium">{quantityDisplay === 'qty' ? 'Qty' : quantityDisplay === 'hours' ? 'Hours' : 'Qty/Hours'}</TableHead>
                      <TableHead className="text-gray-700 font-medium">Rate</TableHead>
                      <TableHead className="text-gray-700 font-medium">Tax</TableHead>
                      <TableHead className="flex items-center gap-1 text-gray-700 font-medium">
                        Amount
                        <Settings className="h-3 w-3 text-gray-500" />
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(formData.items || []).map((item, index) => (
                      <TableRow key={index} className="border-b border-gray-200">
                        <TableCell className="p-3">
                          <Textarea placeholder="Description" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} className="min-h-[60px] resize-none border-gray-300" />
                        </TableCell>
                        <TableCell className="p-3">
                          <Textarea placeholder="Long description" value={item.longDescription} onChange={(e) => handleItemChange(index, 'longDescription', e.target.value)} className="min-h-[60px] resize-none border-gray-300" />
                        </TableCell>
                        <TableCell className="p-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} className="w-16 h-8 border-gray-300" />
                              <div className="flex flex-col">
                                <ChevronUp className="h-3 w-3 text-gray-500 cursor-pointer" />
                                <ChevronDownIcon className="h-3 w-3 text-gray-500 cursor-pointer" />
                              </div>
                            </div>
                            <Input value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)} placeholder="Unit" className="w-16 h-6 text-xs border-gray-300" />
                          </div>
                        </TableCell>
                        <TableCell className="p-3">
                          <div className="flex items-center gap-1">
                            <Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)} className="h-8 border-gray-300" />
                            <div className="flex flex-col">
                              <ChevronUp className="h-3 w-3 text-gray-500 cursor-pointer" />
                              <ChevronDownIcon className="h-3 w-3 text-gray-500 cursor-pointer" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="p-3">
                          <Select value={item.tax} onValueChange={(value) => handleItemChange(index, 'tax', value)}>
                            <SelectTrigger className="h-8 border-gray-300 w-[120px]">
                              <SelectValue placeholder="No Tax" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No Tax">No Tax</SelectItem>
                              <SelectItem value="9%">9%</SelectItem>
                              <SelectItem value="12%">12%</SelectItem>
                              <SelectItem value="18%">18%</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="p-3">
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                            <span>{getCurrencySymbol(formData.currency)}</span>
                            <span>{item.amount.toFixed(2)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            onClick={() => removeItem(index)}
                            disabled={(formData.items || []).length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-white">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="h-10 px-4 border-gray-300 text-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            disabled={loading}
            className="h-10 px-4 bg-gray-900 text-white hover:bg-gray-800"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

